/**
 * API of the products screen (ports of ProductService, ThreadService,
 * StockService and the app-configs call of ConfigService).
 */
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import type { PictureTimestamps, WithId } from '@/shared/api/types';
import { deleteFile, uploadFile } from '@/shared/api/upload';
import type { StoreId } from '@/shared/config/app-config';

const offsetSchema = z.object({
  scale: z.number().nullish(),
  offsetX: z.number().nullish(),
  offsetY: z.number().nullish(),
});

const textPropsSchema = offsetSchema.extend({
  rotate: z.number().nullish(),
  curve: z.number().nullish(),
  letterSpace: z.number().nullish(),
});

export const threadSchema = z.object({
  hex: z.string(),
  name: z.string().nullish(),
  type: z.string().nullish(),
});

export type Thread = z.infer<typeof threadSchema>;

export const sizeSchema = z.object({
  size: z.string().nullish(),
  unique: z.boolean().nullish(),
  ean13Stock: z.string().nullish(),
  ean13CentralFlow: z.string().nullish(),
  m3SkuStock: z.string().nullish(),
  m3SkuCentralFlow: z.string().nullish(),
});

export type ProductSize = z.infer<typeof sizeSchema>;

export const productSchema = z.object({
  id: z.string(),
  reference: z.string().nullish(),
  name: z.string().nullish(),
  displayOrder: z.union([z.number(), z.string()]).nullish(),
  line: z.string().nullish(),
  workshopId: z.string().nullish(),
  workshopName: z.string().nullish(),
  stockControl: z.boolean().nullish(),
  centralStockControl: z.boolean().nullish(),
  centralStockOnly: z.boolean().nullish(),
  hasUniqueSize: z.boolean().nullish(),
  sizes: z.array(sizeSchema).nullish(),
  stores: z.array(z.string()).nullish(),
  textProps: z
    .object({
      threads: z.array(threadSchema).nullish(),
      custom: textPropsSchema.nullish(),
      custom2: textPropsSchema.nullish(),
    })

    .nullish(),
  imageProps: z
    .object({
      main: offsetSchema.nullish(),
      custom: offsetSchema.nullish(),
      custom2: offsetSchema.nullish(),
      custom3: offsetSchema.nullish(),
    })

    .nullish(),
});

export type Product = z.infer<typeof productSchema>;

const pageSchema = z.object({
  data: z.array(productSchema),
  nextCursor: z.string().nullable(),
});

export type ProductPage = z.infer<typeof pageSchema>;

/** POST product/paginate: the selected stores in the body, sort / search / cursor as query. */
export const productPagesQueryOptions = (
  stores: readonly StoreId[],
  search: string,
) =>
  infiniteQueryOptions({
    queryKey: ['products', 'paginate', [...stores], search] as const,
    queryFn: ({ pageParam }) =>
      api.post<ProductPage>(
        'product/paginate',
        { stores },
        {
          query: {
            sortBy: 'displayOrder',
            ...(search ? { search } : {}),
            ...(pageParam ? { after: pageParam } : {}),
          },
          schema: pageSchema,
        },
      ),
    initialPageParam: '' as string,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
  });

export const productQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['products', id] as const,
    queryFn: () =>
      api.get<Product>(`product/${encodeURIComponent(id)}`, {
        schema: productSchema,
      }),
    retry: false,
  });

export const PICTURE_TYPES = ['main', 'custom', 'custom2', 'custom3'] as const;
export type ProductPictureType = (typeof PICTURE_TYPES)[number];

/** Payload of the product dialog. */
export interface ProductInput extends PictureTimestamps<ProductPictureType> {
  reference: string;
  name: string;
  displayOrder: string;
  line: string;
  stockControl: boolean;
  centralStockControl: boolean;
  centralStockOnly: boolean;
  stores: string[];
  workshopId: string | null;
}

/**
 * PUT product is a partial update: the product dialog sends its input, the
 * ABC and additional data dialogs only the fields of the record they edit.
 */
export type ProductUpdate = WithId<
  Partial<Omit<Product, 'id'> & PictureTimestamps<ProductPictureType>>
>;

export const createProduct = (data: ProductInput) =>
  api.post<Product>('product', data);

export const updateProduct = (data: ProductUpdate) =>
  api.put<unknown>('product', data);

export const uploadProductPicture = (
  id: string,
  file: File,
  type: ProductPictureType,
) => uploadFile(`product/${encodeURIComponent(id)}/upload/${type}`, file);

export const deleteProductPicture = (id: string, type: ProductPictureType) =>
  deleteFile(`product/${encodeURIComponent(id)}/${type}`);

export const threadsQueryOptions = () =>
  queryOptions({
    queryKey: ['threads'] as const,
    queryFn: () =>
      api.get<Thread[]>('thread', { schema: z.array(threadSchema) }),
    staleTime: 60_000,
    retry: false,
  });

const stockSchema = z.object({
  ean: z.string().nullish(),
  sku: z.string().nullish(),
  storeCode: z.union([z.string(), z.number()]).nullish(),
  quantity: z.number().nullish(),
});

export type Stock = z.infer<typeof stockSchema>;

/** GET stock?type=centralStock: the central stocks matching the EANs / SKUs. */
export const fetchCentralStocks = (ean: string, sku: string) =>
  api.get<Stock[]>('stock', {
    query: { type: 'centralStock', ean, sku },
    schema: z.array(stockSchema),
  });

/** GET stock?type=stock&limit=-1: the store stocks matching the EANs / SKUs. */
export const fetchStoreStocks = async (ean: string, sku: string) =>
  (
    await api.get<{ data: Stock[] }>('stock', {
      query: { type: 'stock', limit: '-1', ean, sku },
      schema: z.object({ data: z.array(stockSchema) }),
    })
  ).data;
